// 由 GRP 求平面投影轉換，供前端即時顯示重投影誤差與座標換算。
//
// 與 water 的 cv2.findHomography 用的是同一組方程式（DLT 的最小平方解），
// 所以前端算出的誤差與實際計算時一致 —— 現地人員點完 GRP 當下就知道哪一點
// 有問題，不必等試算跑完。
//
// 座標系（與 water/config.py 一致）：
//   世界 (X, Y)   測量成果，公尺，Y 向上
//   影像 (px, py) 畸變校正後的原始影像，原點左上，py 向下
//   正射 (ox, oy) 原點在 (xmin, ymax)，X = xmin + ox·res、Y = ymax − oy·res

/**
 * 是否為有效數值。
 *
 * 不能只用 Number.isFinite(Number(v))：Number(null) 與 Number("") 都是 0，
 * 「尚未指認」的 GRP（px/py 為 null）會被當成影像左上角 (0, 0) 算進
 * homography，把整組轉換拉歪，而且畫面上完全看不出原因。
 */
const isNum = (v) => v !== null && v !== undefined && v !== ""
  && Number.isFinite(Number(v));

/** X、Y、px、py 四個值都齊全的 GRP 才能參與計算。 */
export function isCompleteGrp(g) {
  return !!g && [g.X, g.Y, g.px, g.py].every(isNum);
}

/** 高斯消去法解 A·x = b。回傳 null 表示矩陣奇異（點共線或重複）。 */
function solve(A, b) {
  const n = A.length;
  const M = A.map((row, i) => [...row, b[i]]);
  for (let col = 0; col < n; col += 1) {
    // 部分樞軸：沒有這步，接近共線的點會讓數值完全跑掉
    let pivot = col;
    for (let row = col + 1; row < n; row += 1) {
      if (Math.abs(M[row][col]) > Math.abs(M[pivot][col])) pivot = row;
    }
    if (Math.abs(M[pivot][col]) < 1e-12) return null;
    [M[col], M[pivot]] = [M[pivot], M[col]];
    for (let row = 0; row < n; row += 1) {
      if (row === col) continue;
      const factor = M[row][col] / M[col][col];
      for (let k = col; k <= n; k += 1) M[row][k] -= factor * M[col][k];
    }
  }
  return M.map((row, i) => row[n] / row[i]);
}

/** 3x3 矩陣相乘。 */
function multiply(A, B) {
  return A.map((row, i) =>
    B[0].map((_, j) => row.reduce((sum, value, k) => sum + value * B[k][j], 0)));
}

/**
 * Hartley 等向正規化：平移到質心、縮放成平均距離 √2。
 *
 * 少了這步，DLT 的代數誤差會被座標量級主導 —— 世界座標是 60–85（公尺）、
 * 影像座標是 0–1900（像素），兩者差一個數量級以上，解出來的最小平方解會
 * 偏掉。OpenCV 的 findHomography 內部也做同一件事，有做才能與後端算出
 * 相同的重投影誤差。
 */
function normalize(points) {
  const n = points.length;
  const cx = points.reduce((sum, p) => sum + p[0], 0) / n;
  const cy = points.reduce((sum, p) => sum + p[1], 0) / n;
  const mean = points.reduce(
    (sum, p) => sum + Math.hypot(p[0] - cx, p[1] - cy), 0) / n;
  const scale = mean > 1e-12 ? Math.SQRT2 / mean : 1;
  return {
    T: [[scale, 0, -scale * cx], [0, scale, -scale * cy], [0, 0, 1]],
    points: points.map(([x, y]) => [(x - cx) * scale, (y - cy) * scale]),
  };
}

/**
 * 由 GRP 求 H（世界 → 影像）。
 * grps: [{X, Y, px, py}]，至少 4 個完整的點。回傳 3x3 陣列或 null。
 *
 * 與 water 的 cv2.findHomography(method=0) 相同：正規化 → DLT 最小平方
 * → 還原。前端與後端的重投影誤差因此一致。
 */
export function solveHomography(grps) {
  const valid = (grps || []).filter(isCompleteGrp);
  if (valid.length < 4) return null;

  const src = normalize(valid.map((g) => [+g.X, +g.Y]));
  const dst = normalize(valid.map((g) => [+g.px, +g.py]));

  // 每個對應點兩條方程式；h33 固定為 1，未知數 8 個。
  const rows = [];
  const rhs = [];
  src.points.forEach(([X, Y], i) => {
    const [px, py] = dst.points[i];
    rows.push([X, Y, 1, 0, 0, 0, -px * X, -px * Y]);
    rhs.push(px);
    rows.push([0, 0, 0, X, Y, 1, -py * X, -py * Y]);
    rhs.push(py);
  });

  const AtA = Array.from({ length: 8 }, () => new Array(8).fill(0));
  const Atb = new Array(8).fill(0);
  rows.forEach((row, n) => {
    for (let i = 0; i < 8; i += 1) {
      Atb[i] += row[i] * rhs[n];
      for (let j = 0; j < 8; j += 1) AtA[i][j] += row[i] * row[j];
    }
  });

  const h = solve(AtA, Atb);
  if (!h) return null;
  const Hn = [[h[0], h[1], h[2]], [h[3], h[4], h[5]], [h[6], h[7], 1]];

  // 還原：H = Tdst⁻¹ · Hn · Tsrc
  const linear = multiply(multiply(invert(dst.T), Hn), src.T);
  if (!linear || Math.abs(linear[2][2]) < 1e-12) return null;
  const scaled = linear.map((row) => row.map((value) => value / linear[2][2]));

  return refine(scaled, valid) || scaled;
}

/**
 * 高斯牛頓微調，最小化真實的重投影誤差。
 *
 * DLT 最小化的是代數誤差，與幾何誤差並不等價；OpenCV 的 findHomography
 * 在 DLT 之後同樣會做一次非線性微調。少了這步，前端顯示的逐點誤差會與
 * 後端差到好幾個像素，現地人員會誤判哪一點需要重新指認。
 */
function refine(H0, grps, iterations = 20) {
  let h = [H0[0][0], H0[0][1], H0[0][2],
           H0[1][0], H0[1][1], H0[1][2],
           H0[2][0], H0[2][1]];

  const cost = (params) => {
    let total = 0;
    for (const g of grps) {
      const [X, Y] = [+g.X, +g.Y];
      const w = params[6] * X + params[7] * Y + 1;
      if (Math.abs(w) < 1e-12) return Infinity;
      const dx = (params[0] * X + params[1] * Y + params[2]) / w - +g.px;
      const dy = (params[3] * X + params[4] * Y + params[5]) / w - +g.py;
      total += dx * dx + dy * dy;
    }
    return total;
  };

  let best = cost(h);
  if (!Number.isFinite(best)) return null;

  for (let step = 0; step < iterations; step += 1) {
    const JtJ = Array.from({ length: 8 }, () => new Array(8).fill(0));
    const Jtr = new Array(8).fill(0);

    for (const g of grps) {
      const [X, Y] = [+g.X, +g.Y];
      const w = h[6] * X + h[7] * Y + 1;
      if (Math.abs(w) < 1e-12) return null;
      const u = h[0] * X + h[1] * Y + h[2];
      const v = h[3] * X + h[4] * Y + h[5];
      const rx = u / w - +g.px;
      const ry = v / w - +g.py;

      const jx = [X / w, Y / w, 1 / w, 0, 0, 0,
                  -u * X / (w * w), -u * Y / (w * w)];
      const jy = [0, 0, 0, X / w, Y / w, 1 / w,
                  -v * X / (w * w), -v * Y / (w * w)];

      for (let i = 0; i < 8; i += 1) {
        Jtr[i] += jx[i] * rx + jy[i] * ry;
        for (let j = 0; j < 8; j += 1) {
          JtJ[i][j] += jx[i] * jx[j] + jy[i] * jy[j];
        }
      }
    }

    // 阻尼讓接近奇異時仍可解（Levenberg 的作法）
    for (let i = 0; i < 8; i += 1) JtJ[i][i] *= 1 + 1e-9;

    const delta = solve(JtJ, Jtr.map((value) => -value));
    if (!delta) break;

    const next = h.map((value, i) => value + delta[i]);
    const nextCost = cost(next);
    if (!Number.isFinite(nextCost) || nextCost >= best) break;
    const improvement = (best - nextCost) / best;
    h = next;
    best = nextCost;
    if (improvement < 1e-10) break;
  }

  return [[h[0], h[1], h[2]], [h[3], h[4], h[5]], [h[6], h[7], 1]];
}

/** 以 H 轉換一個點。 */
export function apply(H, x, y) {
  if (!H) return null;
  const w = H[2][0] * x + H[2][1] * y + H[2][2];
  if (Math.abs(w) < 1e-12) return null;
  return {
    x: (H[0][0] * x + H[0][1] * y + H[0][2]) / w,
    y: (H[1][0] * x + H[1][1] * y + H[1][2]) / w,
  };
}

/** 3x3 反矩陣。 */
export function invert(H) {
  if (!H) return null;
  const [[a, b, c], [d, e, f], [g, h, i]] = H;
  const det = a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g);
  if (Math.abs(det) < 1e-12) return null;
  return [
    [(e * i - f * h) / det, (c * h - b * i) / det, (b * f - c * e) / det],
    [(f * g - d * i) / det, (a * i - c * g) / det, (c * d - a * f) / det],
    [(d * h - e * g) / det, (b * g - a * h) / det, (a * e - b * d) / det],
  ];
}

/** 每個 GRP 的重投影誤差（影像像素）。不完整的點回傳 null。 */
export function reprojectionErrors(grps, H) {
  if (!H) return (grps || []).map(() => null);
  return (grps || []).map((g) => {
    if (!isCompleteGrp(g)) return null;
    const projected = apply(H, +g.X, +g.Y);
    if (!projected) return null;
    return Math.hypot(projected.x - +g.px, projected.y - +g.py);
  });
}

/** 影像像素 → 正射像素。需要 H 與正射範圍設定。 */
export function imageToOrtho(H, range, resolution, px, py) {
  const inverse = invert(H);
  if (!inverse || !range || !resolution) return null;
  const world = apply(inverse, px, py);
  if (!world) return null;
  return {
    x: (world.x - +range.xmin) / +resolution,
    y: (+range.ymax - world.y) / +resolution,
  };
}

/** 正射像素 → 影像像素。 */
export function orthoToImage(H, range, resolution, ox, oy) {
  if (!H || !range || !resolution) return null;
  return apply(H,
    +range.xmin + ox * +resolution,
    +range.ymax - oy * +resolution);
}

/**
 * 由 GRP 的世界座標建議一組正射範圍：取外接矩形再外擴 10%。
 * 現地人員通常知道要涵蓋哪一段河道，但不會想自己算邊界。
 */
export function suggestRange(grps) {
  const points = (grps || []).filter((g) => isNum(g.X) && isNum(g.Y));
  if (points.length < 2) return null;
  const xs = points.map((g) => +g.X);
  const ys = points.map((g) => +g.Y);
  const padX = (Math.max(...xs) - Math.min(...xs)) * 0.1 || 1;
  const padY = (Math.max(...ys) - Math.min(...ys)) * 0.1 || 1;
  const round = (value) => Math.round(value * 10) / 10;
  return {
    xmin: round(Math.min(...xs) - padX),
    xmax: round(Math.max(...xs) + padX),
    ymin: round(Math.min(...ys) - padY),
    ymax: round(Math.max(...ys) + padY),
  };
}